import React, { useMemo, useState } from "react";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import { platformErrorsFormat } from "@/features/observability/platform-errors/components/platform-errors-format";
import { UNREGISTERED_APP, type PlatformErrorGroup } from "@/features/observability/platform-errors/types/platform-errors.types";

type Props = {
    groups: PlatformErrorGroup[];
    asOf: number;
    lowerBound: boolean;
    stale: boolean;
    truncated: boolean;
    selectedKey: string | null;
    onOpen: (group: PlatformErrorGroup) => void;
};

// Events are read by (service, fingerprint): without either, the drawer could only mix unrelated events.
const blockedReason = (group: PlatformErrorGroup): string | null => {
    if (group.service === null) return "No se puede abrir: sin servicio, sus eventos se mezclarían con los de todos los servicios.";
    if (group.fingerprint === null) return "No se puede abrir: sin fingerprint, sus eventos se mezclarían con los de otros fallos.";
    return null;
};

// Only a search can make a measured list "not match"; an empty measured list says what it is worth.
const emptyMessage = (searching: boolean, stale: boolean, lowerBound: boolean): string => {
    if (searching) return "Ningún grupo coincide con la búsqueda.";
    if (stale) return "Sin grupos medidos (datos obsoletos): puede haber errores que aún no se ven.";
    if (lowerBound) return "Sin grupos medidos (datos parciales): esto no significa que no haya errores.";
    return "Sin grupos en este periodo.";
};

type SortKey = "count" | "first" | "last";

const PAGE_SIZE = 20;

const SORT_VALUE: Record<SortKey, (group: PlatformErrorGroup) => number | null> = {
    count: (group) => group.count,
    first: (group) => group.firstSeenAt,
    last: (group) => group.lastSeenAt,
};

// Unknown values sort after every known one in both directions: a missing date is never "the newest".
const compare = (key: SortKey, descending: boolean) => (a: PlatformErrorGroup, b: PlatformErrorGroup): number => {
    const left = SORT_VALUE[key](a);
    const right = SORT_VALUE[key](b);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return descending ? right - left : left - right;
};

const matches = (group: PlatformErrorGroup, needle: string): boolean =>
    [group.service, group.errorCode, group.errorClass, group.lastMessage].some(
        (value) => value !== null && value.toLowerCase().includes(needle),
    );

// The backend groups by (service, fingerprint): the same fingerprint can head a row per service.
const groupKey = (group: PlatformErrorGroup): string => JSON.stringify([group.service, group.fingerprint]);

export function PlatformErrorsGroupsTable({ groups, asOf, lowerBound, stale, truncated, selectedKey, onOpen }: Props) {
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({ key: "count", descending: true });
    const [page, setPage] = useState(0);

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        const filtered = needle === "" ? groups : groups.filter((group) => matches(group, needle));
        return filtered.slice().sort(compare(sort.key, sort.descending));
    }, [groups, search, sort]);

    const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const current = Math.min(page, pages - 1);
    const visible = rows.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);

    const sortBy = (key: SortKey): void => {
        setSort((previous) => ({ key, descending: previous.key === key ? !previous.descending : true }));
        setPage(0);
    };

    const indicator = (key: SortKey): string => (sort.key !== key ? "" : sort.descending ? " ↓" : " ↑");

    const ariaSort = (key: SortKey): "ascending" | "descending" | "none" =>
        sort.key !== key ? "none" : sort.descending ? "descending" : "ascending";

    return (
        <div className="fe-card fe-groups">
            <div className="fe-card__hd fe-groups__hd">
                <div className="fe-groups__title">
                    <div className="fe-card__title">Grupos</div>
                    <div className="fe-muted">{`${frontendErrorsFormat.count(rows.length)} grupos (servicio · fingerprint) · clic para ver eventos`}</div>
                </div>
                <label className="fe-search">
                    <span className="material-symbols-outlined" aria-hidden="true">search</span>
                    <input
                        aria-label="Buscar servicio, código, clase o mensaje"
                        placeholder="Buscar servicio, código, clase o mensaje"
                        value={search}
                        onChange={(event) => {
                            setSearch(event.target.value);
                            setPage(0);
                        }}
                    />
                </label>
            </div>
            {truncated ? (
                <div className="fe-notice fe-notice--warn fe-groups__notice">
                    <span className="material-symbols-outlined" aria-hidden="true">warning</span>
                    Hay más grupos de los que se muestran: la fuente recortó la lista.
                </div>
            ) : null}
            <div className="tbl-wrap fe-groups__wrap">
                <table className="tbl fe-groups__tbl">
                    <thead>
                        <tr>
                            <th>Servicio</th>
                            <th>Severidad</th>
                            <th>Código de error</th>
                            <th>Fingerprint</th>
                            <th className="td-num" aria-sort={ariaSort("count")}>
                                <button type="button" className="fe-sort" onClick={() => sortBy("count")}>{`Ocurrencias${indicator("count")}`}</button>
                            </th>
                            <th aria-sort={ariaSort("first")}>
                                <button type="button" className="fe-sort" onClick={() => sortBy("first")}>{`Primera vez${indicator("first")}`}</button>
                            </th>
                            <th aria-sort={ariaSort("last")}>
                                <button type="button" className="fe-sort" onClick={() => sortBy("last")}>{`Última vez${indicator("last")}`}</button>
                            </th>
                            <th>Último mensaje</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((group, index) => {
                            const key = groupKey(group);
                            const reason = blockedReason(group);
                            const openable = reason === null;
                            const reasonId = `pe-group-reason-${current * PAGE_SIZE + index}`;
                            const severity = platformErrorsFormat.severityStyle(group.severity);
                            return (
                                // The row click is a mouse shortcut; keyboard and assistive tech use the cell's button.
                                <tr
                                    key={key}
                                    data-testid="pe-group-row"
                                    className={`fe-groups__row${openable ? " fe-groups__row--open" : ""}${openable && key === selectedKey ? " fe-groups__row--selected" : ""}`}
                                    onClick={() => {
                                        if (openable) onOpen(group);
                                    }}
                                >
                                    <td className="td-strong fe-ellipsis">
                                        {/* aria-disabled, not disabled: the button stays focusable so its reason can be reached. */}
                                        <button
                                            type="button"
                                            className="fe-link"
                                            aria-disabled={openable ? undefined : true}
                                            aria-describedby={openable ? undefined : reasonId}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                if (openable) onOpen(group);
                                            }}
                                        >
                                            {group.service === null ? "Sin servicio" : group.service}
                                        </button>
                                        {reason === null ? null : <div id={reasonId} className="fe-muted">{reason}</div>}
                                        {group.app === UNREGISTERED_APP ? (
                                            <>
                                                {" "}
                                                <span className="fe-tag fe-tag--estimate" title="El servicio no pertenece a ninguna app registrada">No registrado</span>
                                            </>
                                        ) : null}
                                    </td>
                                    <td>
                                        <span className="fe-chip">
                                            <span className="fe-swatch" style={{ background: severity.color }} />
                                            {severity.label}
                                        </span>
                                    </td>
                                    <td className="td-muted fe-ellipsis">{group.errorCode === null ? "—" : group.errorCode}</td>
                                    <td className="td-muted"><span className="fe-code-chip" title={group.fingerprint === null ? undefined : group.fingerprint}>{frontendErrorsFormat.short(group.fingerprint)}</span></td>
                                    <td className="td-num td-strong">{`${frontendErrorsFormat.count(group.count)}${lowerBound ? " (mín.)" : ""}`}</td>
                                    <td className="td-muted">{frontendErrorsFormat.nullableDateTime(group.firstSeenAt)}</td>
                                    <td className="td-muted">{group.lastSeenAt === null ? "—" : frontendErrorsFormat.sinceAsOf(group.lastSeenAt, asOf)}</td>
                                    <td className="td-muted fe-ellipsis" title={group.lastMessage === null ? undefined : group.lastMessage}>
                                        {group.lastMessage === null ? "—" : group.lastMessage}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {rows.length === 0 ? <div className="fe-groups__empty">{emptyMessage(search.trim() !== "", stale, lowerBound)}</div> : null}
            </div>
            <div className="fe-pager">
                <span>{rows.length === 0 ? "" : `${current * PAGE_SIZE + 1}–${Math.min(rows.length, current * PAGE_SIZE + PAGE_SIZE)} de ${rows.length}`}</span>
                <div className="fe-pager__btns">
                    <button type="button" className="btn btn--icon btn--sm" aria-label="Página anterior" disabled={current === 0} onClick={() => setPage(current - 1)}>
                        <span className="material-symbols-outlined" aria-hidden="true">chevron_left</span>
                    </button>
                    <button type="button" className="btn btn--icon btn--sm" aria-label="Página siguiente" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
                        <span className="material-symbols-outlined" aria-hidden="true">chevron_right</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

export const platformErrorsGroupKey = groupKey;
